import { forwardRef, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import { HTMLMotionProps, useAnimation } from "motion/react";
import BrandclubVideo from "../../../BrandclubVideo";
import {
  InfluencerHeadWrapper,
  InfluencerHeaderVideoWrapper,
  InfluencerSiteWrapper,
  InfluencerSubtitle,
  InfluencerTeachSectionWrapper,
  InfluencerHeadTextWrapper,
  InfluencerTitle,
  StyledTextTheApp,
  InfluencerQuoteWrapper,
  InfluencerTeachSectionContentWrapper,
  TeachTileImage,
  InfluencerTeachSectionTitle,
  InfluencerTeachTileWrapper,
  InfluencerDetailSectionWrapper,
  InfluencerShareSectionWrapper,
  InfluencerDetailSectionTitle,
  InfluencerDetailSectionDescription,
  InfluencerDetailSectionTextWrapper,
  InfluencerDetailSectionImageWrapper,
  InfluencerShareSectionImage,
  InfluencerShareSectionQuote,
  InfluencerTeachTileTitle,
  InfluencerTeachTileDescription,
  InfluencerDetailSectionImage,
  InfluencerDetailReverseSectionWrapper,
} from "./styled";
import { PagesButton } from "../pagesComponents/PagesButton";

const TeachCashImg = forwardRef<HTMLImageElement, HTMLMotionProps<"img">>(
  (props, ref) => {
    return (
      <TeachTileImage
        {...props}
        ref={ref}
        initial="hidden"
        transition={{ duration: 1, ease: "easeInOut" }}
        variants={{
          visible: { opacity: 1, y: 0 },
          hidden: { opacity: 0, y: 100 },
        }}
        alt="cash"
        src="https://media.brandclub.com/brandclub/image_asset/teach_cash.png"
      />
    );
  }
);

const TeachSocialMediaImage = forwardRef<
  HTMLImageElement,
  HTMLMotionProps<"img">
>((props, ref) => {
  return (
    <TeachTileImage
      {...props}
      ref={ref}
      initial="hidden"
      transition={{ duration: 1, ease: "easeInOut" }}
      variants={{
        visible: { opacity: 1, scale: 1, rotate: 0 },
        hidden: { opacity: 0, scale: 0.5, rotate: 80 },
      }}
      alt="cash"
      src="https://media.brandclub.com/brandclub/image_asset/influencer_medias.png"
    />
  );
});

const TeachInfluencerPhone = forwardRef<
  HTMLImageElement,
  HTMLMotionProps<"img">
>((props, ref) => {
  return (
    <TeachTileImage
      {...props}
      ref={ref}
      initial="hidden"
      transition={{ duration: 1, ease: "easeInOut" }}
      variants={{
        visible: { opacity: 1, rotate: 0, scale: 1 },
        hidden: { opacity: 0, rotate: 20, scale: 0.5 },
      }}
      alt="download"
      src="https://media.brandclub.com/brandclub/image_asset/influencer_phoneapp.png"
    />
  );
});

interface TeachTileProps {
  header: string;
  desc: React.ReactNode;
  backgroundColor: string;
  className?: string;
}
const TeachTile = ({
  header,
  desc,
  backgroundColor,
  ...props
}: TeachTileProps) => {
  const controls = useAnimation();
  const [ref, inView] = useInView();
  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  return (
    <InfluencerTeachTileWrapper {...props}>
      <div className="img">
        <div className="img_bg" style={{ backgroundColor }} />
        <div className="tophalf"></div>
        {header === "Earn" && (
          <TeachCashImg
            ref={ref}
            animate={controls}
            style={{ width: "100%" }}
          />
        )}
        {header === "Get cash back" && (
          <TeachCashImg
            ref={ref}
            animate={controls}
            style={{ width: "100%" }}
          />
        )}
        <div className="bothalf"></div>
        {header === "Select" && (
          <TeachInfluencerPhone ref={ref} animate={controls} />
        )}
        {header === "Share" && (
          <TeachSocialMediaImage ref={ref} animate={controls} />
        )}
      </div>
      <InfluencerTeachTileTitle>{header}</InfluencerTeachTileTitle>
      <InfluencerTeachTileDescription>{desc}</InfluencerTeachTileDescription>
    </InfluencerTeachTileWrapper>
  );
};

interface Video {
  video: string;
  posterUrl: string;
}

const HEADER_VIDEO: Video = {
  video: "https://media.brandclub.com/brandclub/media/influencer_head.mp4",
  posterUrl:
    "https://media.brandclub.com/brandclub/media/cover/influencer_head.png",
};
const HeadSection = () => {
  return (
    <InfluencerHeadWrapper>
      <InfluencerHeadTextWrapper
        animate={{ opacity: [0, 1], y: [100, 0] }}
        transition={{ ease: "easeInOut", duration: 1 }}
      >
        <InfluencerTitle>
          Promote the
          <br />
          best brands
        </InfluencerTitle>
        <InfluencerSubtitle>
          Earn up to 20% affiliate commission across millions of popular
          products.
        </InfluencerSubtitle>
        <StyledTextTheApp />
      </InfluencerHeadTextWrapper>
      <InfluencerHeaderVideoWrapper>
        <BrandclubVideo
          posterUrl={HEADER_VIDEO.posterUrl}
          videoUrl={HEADER_VIDEO.video}
        ></BrandclubVideo>
        <img
          src="https://media.brandclub.com/brandclub/image_asset/icon-bullet-6.png"
          srcSet="
          https://media.brandclub.com/brandclub/image_asset/icon-bullet-6.png 1x,
          https://media.brandclub.com/brandclub/image_asset/icon-bullet-6@2x.png 2x,
          https://media.brandclub.com/brandclub/image_asset/icon-bullet-6@3x.png 3x,
          "
          alt="icon-bullet-6"
          className="hexagon_logo"
        />
      </InfluencerHeaderVideoWrapper>
    </InfluencerHeadWrapper>
  );
};

const HomeTeachSection = () => {
  return (
    <InfluencerTeachSectionWrapper>
      <InfluencerTeachSectionTitle
        animate={{ opacity: [0, 1], y: [100, 0] }}
        transition={{ ease: "easeInOut", duration: 1 }}
      >
        How it works
      </InfluencerTeachSectionTitle>
      <InfluencerTeachSectionContentWrapper>
        <TeachTile
          backgroundColor="#FFC5C566"
          header="Select"
          desc="Choose popular products from the biggest retailers."
        />
        <TeachTile
          backgroundColor="#E5E5E566"
          header="Share"
          desc="Feature brands and products with your community."
        />
        <TeachTile
          backgroundColor="#4E74D066"
          header="Earn"
          desc="Get instant cash payouts for every referral."
        />
      </InfluencerTeachSectionContentWrapper>
    </InfluencerTeachSectionWrapper>
  );
};

interface DetailSectionProps {
  buttonText: string;
  buttonOnClick: () => void;
  className?: string;
  src: string;
  srcSet: string;
  title: React.ReactNode;
  desc: React.ReactNode;
}
const Detail1Section = ({
  buttonText,
  buttonOnClick,
  src,
  srcSet,
  title,
  desc,
  ...props
}: DetailSectionProps) => {
  return (
    <InfluencerDetailSectionWrapper {...props}>
      <InfluencerDetailSectionTextWrapper>
        <InfluencerDetailSectionTitle>{title}</InfluencerDetailSectionTitle>
        <InfluencerDetailSectionDescription>
          {desc}
        </InfluencerDetailSectionDescription>
        <PagesButton onClick={buttonOnClick}>{buttonText}</PagesButton>
      </InfluencerDetailSectionTextWrapper>
      <InfluencerDetailSectionImageWrapper>
        <InfluencerDetailSectionImage
          className="phone_img"
          src={src}
          srcSet={srcSet}
          alt="detail_phone1"
        />
      </InfluencerDetailSectionImageWrapper>
    </InfluencerDetailSectionWrapper>
  );
};

const Detail2Section = ({
  buttonText,
  buttonOnClick,
  src,
  srcSet,
  title,
  desc,
  ...props
}: DetailSectionProps) => {
  return (
    <InfluencerDetailReverseSectionWrapper {...props}>
      <InfluencerDetailSectionTextWrapper>
        <InfluencerDetailSectionTitle>{title}</InfluencerDetailSectionTitle>
        <InfluencerDetailSectionDescription>
          {desc}
        </InfluencerDetailSectionDescription>
        <PagesButton onClick={buttonOnClick}>{buttonText}</PagesButton>
      </InfluencerDetailSectionTextWrapper>
      <InfluencerDetailSectionImageWrapper>
        <InfluencerDetailSectionImage
          className="phone_img"
          src={src}
          srcSet={srcSet}
          alt="detail_phone1"
        />
      </InfluencerDetailSectionImageWrapper>
    </InfluencerDetailReverseSectionWrapper>
  );
};

interface ShareSectionProps {
  className?: string;
  imgUrl: string;
}
const ShareSection = ({ imgUrl, ...props }: ShareSectionProps) => {
  return (
    <InfluencerShareSectionWrapper {...props}>
      <InfluencerShareSectionImage src={imgUrl} alt="share_sec_bg" />
      <InfluencerQuoteWrapper>
        <img
          className="img-quote"
          alt="quotations"
          src="https://media.brandclub.com/brandclub/image_asset/quote.png"
        />
        <InfluencerShareSectionQuote>
          Brandclub makes it easy to become an influencer for thousands of
        brands and earn high commission rates.
        </InfluencerShareSectionQuote>
        <span>Angela W.</span>
      </InfluencerQuoteWrapper>
    </InfluencerShareSectionWrapper>
  );
};

const Influencer = () => {
  const navigate = useNavigate();
  return (
    <InfluencerSiteWrapper className="influencer_site">
      <Helmet>
        <title>Influencer - Brandclub</title>
      </Helmet>
      <HeadSection />
      <HomeTeachSection />
      <Detail1Section
        className="influencer1"
        src="https://media.brandclub.com/brandclub/image_asset/my-clubs-influencer.png"
        srcSet="
          https://media.brandclub.com/brandclub/image_asset/my-clubs-influencer.png 1x,
          https://media.brandclub.com/brandclub/image_asset/my-clubs-influencer@2x.png 2x,
          https://media.brandclub.com/brandclub/image_asset/my-clubs-influencer@3x.png 3x,
        "
        title="Access the world’s biggest brands"
        desc="Instantly become an influencer for thousands of the most popular brands - all within a single unified app."
        buttonText="Get started"
        buttonOnClick={() => {
          navigate("/getapp");
        }}
      />
      <Detail2Section
        className="influencer2"
        src="https://media.brandclub.com/brandclub/image_asset/my-clubs-influencer.png"
        srcSet="
          https://media.brandclub.com/brandclub/image_asset/share-influencer.png 1x,
          https://media.brandclub.com/brandclub/image_asset/share-influencer@2x.png 2x,
          https://media.brandclub.com/brandclub/image_asset/share-influencer@3x.png 3x,
        "
        title="Share your links anywhere"
        desc="In a single click, you can generate a unique affiliate link that you can share in social posts, text messages, emails, or anywhere you choose."
        buttonText="Join now"
        buttonOnClick={() => {
          navigate("/getapp");
        }}
      />
      <Detail1Section
        className="influencer1"
        src="https://media.brandclub.com/brandclub/image_asset/retailers-influencer.png"
        srcSet="
          https://media.brandclub.com/brandclub/image_asset/retailers-influencer.png 1x,
          https://media.brandclub.com/brandclub/image_asset/retailers-influencer@2x.png 2x,
          https://media.brandclub.com/brandclub/image_asset/retailers-influencer@3x.png 3x,
        "
        title="Allow your fans to shop everywhere"
        desc="Shoppers love to buy from Amazon, Walmart, Target, and other leading retailers. Brandclub earns you commission wherever your customers shop."
        buttonText="Sign up"
        buttonOnClick={() => {
          navigate("/getapp");
        }}
      />
      <Detail2Section
        className="influencer2"
        src="https://media.brandclub.com/brandclub/image_asset/payout-influencer.png"
        srcSet="
          https://media.brandclub.com/brandclub/image_asset/payout-influencer.png 1x,
          https://media.brandclub.com/brandclub/image_asset/payout-influencer@2x.png 2x,
          https://media.brandclub.com/brandclub/image_asset/payout-influencer@3x.png 3x,
        "
        title="Get instant cash payouts"
        desc="No more waiting for affiliate commissions. Brandclub sends you cash in real-time that you can easily transfer to PayPal or Venmo."
        buttonText="Get the app"
        buttonOnClick={() => {
          navigate("/getapp");
        }}
      />
      <ShareSection imgUrl="https://media.brandclub.com/brandclub/image_asset/influencer_people.png" />
    </InfluencerSiteWrapper>
  );
};

export default Influencer;
